import React from 'react';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  applyStyle,
  buildStylesFromArray,
} from 'Components/ComponentLibrary/DesignSystem/utils';
import {
  paddingConverter,
  paddingDefaultProps,
  paddingPropTypes,
} from 'Components/ComponentLibrary/DesignSystem/padding';

const SIZES = ['sm', 'md', 'lg'];
const CONTENT_WIDTH_PREFIX = 'contentWidth';

const useStyles = buildUseStyles('StackItem', theme => ({
  root: {
    display: 'block',
    padding: props => paddingConverter(theme, props),
    background: ({ background }) => {
      switch (background) {
        case 'white':
          return theme.palette.common.white;
        case 'lightGray':
          return theme.palette.facet.lightGray5;
        case 'primaryBlueMuted':
          return theme.palette.facet.primaryBlueMuted;
        case 'primaryGradient':
          return theme.gradients.vertical.primaryToMutedBlue;
        default:
          return undefined;
      }
    },
  },
  rootZeroFontSize: {
    fontSize: '0',
  },
  rootHidden: {
    display: 'none',
  },
  rootFillSpace: {
    flexGrow: 1,
    minWidth: 0,
  },
  rootScrollable: {
    overflowY: 'auto',
  },
  rootUnshrinkable: {
    flexShrink: 0,
  },
  rootPositionTop: {
    position: 'sticky',
    top: 0,
    zIndex: theme.zIndex.appBar,
  },
  rootPositionBottom: {
    position: 'sticky',
    bottom: 0,
    zIndex: theme.zIndex.appBar,
  },
  content: {
    margin: '0 auto',
  },
  ...buildStylesFromArray(SIZES, CONTENT_WIDTH_PREFIX, size => {
    return {
      maxWidth: theme.dimensions[size],
    };
  }),
}));

/**
 * Stack Item. Replace with the Facet Design <FDStack /> component.
 * @deprecated
 */
const StackItem = (props) => {
  const {
    children: childrenProp,
    contentWidth,
    fillsSpace,
    scrollable,
    hidden,
    unshrinkable,
    position,
    useZeroFontSize,
  } = props;

  const classes = useStyles(props);

  let children = childrenProp;
  if (contentWidth !== 'default') {
    children = (
      <div
        className={classNames(classes.content, applyStyle(classes, 'contentWidth', contentWidth))}
      >
        {childrenProp}
      </div>
    );
  }

  return (
    <Box
      className={classNames(classes.root, {
        [classes.rootHidden]: hidden,
        [classes.rootZeroFontSize]: useZeroFontSize,
        [classes.rootScrollable]: scrollable,
        [classes.rootFillSpace]: fillsSpace,
        [classes.rootUnshrinkable]: unshrinkable,
        [classes.rootPositionTop]: position === 'top',
        [classes.rootPositionBottom]: position === 'bottom',
      })}
    >
      {children}
    </Box>
  );
};

StackItem.defaultProps = {
  ...paddingDefaultProps,
  children: null,
  padding: null,
  fillsSpace: false,
  unshrinkable: false,
  scrollable: false,
  hidden: false,
  useZeroFontSize: true,
  contentWidth: 'default',
  position: 'default',
  background: 'default',
};

StackItem.propTypes = {
  ...paddingPropTypes,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  padding: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.number),
  ]),
  paddingVertical: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.number),
  ]),
  paddingHorizontal: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.number),
  ]),
  paddingTop: PropTypes.number,
  useZeroFontSize: PropTypes.bool,
  paddingBottom: PropTypes.number,
  paddingLeft: PropTypes.number,
  paddingRight: PropTypes.number,
  hidden: PropTypes.bool,
  fillsSpace: PropTypes.bool,
  unshrinkable: PropTypes.bool,
  scrollable: PropTypes.bool,
  contentWidth: PropTypes.oneOf([...SIZES, 'default']),
  position: PropTypes.oneOf(['top', 'bottom', 'default']),
  background: PropTypes.oneOf(['white', 'lightGray', 'primaryGradient', 'primaryBlueMuted', 'default']),
};

export default StackItem;
